import React, { useEffect, useRef, useState } from 'react';

const LEGEND_API = 'https://gbank.gsj.jp/seamless/v2/api/1.2/legend.json';

const detectLang = () => {
	const language = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) || 'ja';
	return language.startsWith('ja') ? 'ja' : 'en';
};

const SeamlessLegendPanel = ({ map, visible }) => {
	const [legend, setLegend] = useState(null);
	const [error, setError] = useState(null);
	const [collapsed, setCollapsed] = useState(false);
	const reqIdRef = useRef(0);
	const lang = detectLang();

	const labels = lang === 'ja'
		? { title: 'シームレス地質図 凡例', loading: '読み込み中…', noData: '凡例情報がありません', error: '凡例の取得に失敗しました', formationAge: '形成時代', group: '大区分', lithology: '岩相', symbol: '記号' }
		: { title: 'Seamless Geology Legend', loading: 'Loading…', noData: 'No legend at this point', error: 'Failed to load legend', formationAge: 'Formation age', group: 'Group', lithology: 'Lithology', symbol: 'Symbol' };

	useEffect(() => {
		if (!map || !visible) return undefined;

		let cancelled = false;

		const fetchLegend = async () => {
			const center = map.getCenter();
			const zoom = Math.min(12, Math.max(3, Math.round(map.getZoom())));
			const myReqId = ++reqIdRef.current;
			const url = `${LEGEND_API}?point=${center.lat.toFixed(6)},${center.lng.toFixed(6)}&z=${zoom}&lang=${lang}`;
			try {
				const res = await fetch(url);
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				const json = await res.json();
				if (cancelled || myReqId !== reqIdRef.current) return;
				const entry = Array.isArray(json) ? (json[0] || null) : json;
				setLegend(entry || null);
				setError(null);
			} catch (e) {
				if (cancelled || myReqId !== reqIdRef.current) return;
				console.warn('[SeamlessLegend] fetch failed', e);
				setLegend(null);
				setError(e);
			}
		};

		fetchLegend();
		map.on('moveend', fetchLegend);
		return () => {
			cancelled = true;
			map.off('moveend', fetchLegend);
		};
	}, [map, visible, lang]);

	if (!visible) return null;

	const colorFromLegend = (l) => {
		if (!l) return null;
		if (typeof l.value === 'string' && l.value.length > 0) return l.value.startsWith('#') ? l.value : `#${l.value}`;
		if (l.r != null && l.g != null && l.b != null) return `rgb(${l.r}, ${l.g}, ${l.b})`;
		return null;
	};

	const fmtAge = legend && (legend[`formationAge_${lang}`] || legend.formationAge_ja || legend.formationAge_en || legend.formationAge);
	const fmtGroup = legend && (legend[`group_${lang}`] || legend.group_ja || legend.group_en || legend.group);
	const fmtLith = legend && (legend[`lithology_${lang}`] || legend.lithology_ja || legend.lithology_en || legend.lithology);
	const fmtSymbol = legend && legend.symbol;
	const fmtTitle = legend && (legend[`title_${lang}`] || legend.title);
	const swatch = colorFromLegend(legend);

	return (
		<div id="seamlessLegend" className={collapsed ? 'collapsed' : ''}>
			<div className="seamlessLegend-header">
				<span className="seamlessLegend-title">{labels.title}</span>
				<button
					type="button"
					className="seamlessLegend-toggle"
					onClick={() => setCollapsed((v) => !v)}
					aria-label={collapsed ? 'expand' : 'collapse'}
				>
					{collapsed ? '▲' : '▼'}
				</button>
			</div>
			{!collapsed && (
				<div className="seamlessLegend-body">
					{error && !legend && (
						<div className="seamlessLegend-message">{labels.error}</div>
					)}
					{!error && !legend && (
						<div className="seamlessLegend-message">{labels.loading}</div>
					)}
					{legend && (
						<div className="seamlessLegend-row">
							{swatch && (
								<span
									className="seamlessLegend-swatch"
									style={{ backgroundColor: swatch }}
								/>
							)}
							<div className="seamlessLegend-info">
								{fmtTitle && (
									<div className="seamlessLegend-name">{fmtTitle}</div>
								)}
								{fmtSymbol && (
									<div className="seamlessLegend-line"><span className="seamlessLegend-key">{labels.symbol}:</span> {fmtSymbol}</div>
								)}
								{fmtAge && (
									<div className="seamlessLegend-line"><span className="seamlessLegend-key">{labels.formationAge}:</span> {fmtAge}</div>
								)}
								{fmtGroup && (
									<div className="seamlessLegend-line"><span className="seamlessLegend-key">{labels.group}:</span> {fmtGroup}</div>
								)}
								{fmtLith && (
									<div className="seamlessLegend-line"><span className="seamlessLegend-key">{labels.lithology}:</span> {fmtLith}</div>
								)}
								{!fmtTitle && !fmtSymbol && !fmtAge && !fmtGroup && !fmtLith && (
									<div className="seamlessLegend-message">{labels.noData}</div>
								)}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default SeamlessLegendPanel;
